import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Clipboard, Download, RefreshCw } from 'lucide-react';
import { Button, Card, EmptyState, toast } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { useAccessibleProducts } from '../../../hooks/useAccessibleProducts';
import {
  useAccountantReport,
  useClosingAccountantReport,
  useSyncClosingAccountantReport,
} from '../../../hooks/useFinancialOps';
import { financialOpsService } from '../../../services/financial-ops.service';
import { formatCurrency, formatDateBR } from '../../../utils/format';
import type {
  AccountantReport,
  AccountantReportCoverage,
  AccountantReportEvent,
} from '@crm/shared';

const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];
const TABS = [
  ['summary', 'Resumo'],
  ['users', 'Billing por usuário'],
  ['subscriptions', 'Assinaturas'],
  ['problems', 'Pagamentos problemáticos'],
  ['refunds', 'Reembolsos e disputas'],
  ['payouts', 'Payouts e conciliação'],
] as const;

function monthValue(month: number, year: number) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function currentMonth() {
  const now = new Date();
  return monthValue(now.getMonth() + 1, now.getFullYear());
}

function periodLabel(report: AccountantReport) {
  const start = report.start;
  const end = report.end;
  return `${MONTHS[start.month - 1]}/${start.year}${start.month === end.month && start.year === end.year ? '' : ` a ${MONTHS[end.month - 1]}/${end.year}`}`;
}

function errorMessage(error: unknown, fallback: string) {
  if (!error || typeof error !== 'object') {
    return fallback;
  }
  const candidate = error as { message?: string; response?: { data?: { message?: string } } };
  return candidate.response?.data?.message || candidate.message || fallback;
}

function coverageDetails(coverage: AccountantReportCoverage | null | undefined) {
  const exclusions = Object.values(coverage?.exclusions ?? {});
  return {
    excludedCount: Number(coverage?.excludedCount ?? 0),
    excludedAmount: exclusions.reduce(
      (total, exclusion) => total + Number(exclusion.amount ?? 0),
      0
    ),
    lastSyncAt: coverage?.lastSyncAt ?? null,
  };
}

function statusCount(value: number | null) {
  return value === null ? 'Não disponível' : value;
}

function StripeReference({ event }: { event: AccountantReportEvent }) {
  const id =
    event.stripeInvoiceId ||
    event.stripePaymentIntentId ||
    event.stripeChargeId ||
    event.stripeSubscriptionId ||
    event.stripeCustomerId ||
    event.stripePayoutId;
  if (!id) {
    return <span className="text-ink-tertiary">—</span>;
  }
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 font-mono text-xs text-ink-secondary hover:text-ink"
      onClick={() =>
        navigator.clipboard
          ?.writeText(id)
          .then(() => toast.success('ID Stripe copiado.'))
          .catch(() => toast.error('Não foi possível copiar o ID Stripe.'))
      }
      title="Copiar ID Stripe"
    >
      {id} <Clipboard size={12} />
    </button>
  );
}

function EventTable({ events, empty }: { events: AccountantReportEvent[]; empty: string }) {
  if (!events.length) {
    return (
      <EmptyState
        title={empty}
        body="Os dados apresentados são somente os campos auditáveis do ledger."
        className="py-8"
      />
    );
  }
  return (
    <div className="overflow-x-auto border border-border rounded-lg">
      <table className="w-full text-sm">
        <thead className="bg-subtle text-xs text-ink-secondary uppercase tracking-wider">
          <tr>
            <th className="px-3 py-2 text-left">Data</th>
            <th className="px-3 py-2 text-left">Usuário</th>
            <th className="px-3 py-2 text-left">E-mail</th>
            <th className="px-3 py-2 text-left">Plano</th>
            <th className="px-3 py-2 text-left">Status assinatura</th>
            <th className="px-3 py-2 text-left">Ciclo</th>
            <th className="px-3 py-2 text-left">Tipo</th>
            <th className="px-3 py-2 text-left">Status</th>
            <th className="px-3 py-2 text-left">Referência Stripe</th>
            <th className="px-3 py-2 text-right">Bruto</th>
            <th className="px-3 py-2 text-right">Taxa</th>
            <th className="px-3 py-2 text-right">Líquido</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event, index) => (
            <tr key={event.id ?? event.stripeEventId ?? index} className="border-t border-border">
              <td className="px-3 py-2 whitespace-nowrap">
                {formatDateBR(event.availableAt || event.occurredAt)}
              </td>
              <td className="px-3 py-2">{event.customerName || '—'}</td>
              <td className="px-3 py-2">{event.customerEmail || '—'}</td>
              <td className="px-3 py-2">{event.planName || '—'}</td>
              <td className="px-3 py-2">{event.subscriptionStatus || '—'}</td>
              <td className="px-3 py-2">{event.billingInterval || '—'}</td>
              <td className="px-3 py-2">{String(event.type || '—').replace(/_/g, ' ')}</td>
              <td className="px-3 py-2">{event.status || '—'}</td>
              <td className="px-3 py-2">
                <StripeReference event={event} />
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {formatCurrency(Math.abs(event.grossAmount))}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {formatCurrency(Math.abs(event.stripeFeeAmount))}
              </td>
              <td className="px-3 py-2 text-right whitespace-nowrap">
                {formatCurrency(event.netAmount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AccountantReportPage() {
  const { closingId } = useParams<{ closingId: string }>();
  const { data: products = [] } = useAccessibleProducts();
  const [productId, setProductId] = useState<number | null>(null);
  const [start, setStart] = useState(currentMonth);
  const [end, setEnd] = useState(currentMonth);
  const [tab, setTab] = useState<(typeof TABS)[number][0]>('summary');
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null);
  const invalidRange = useMemo(() => {
    const [sy, sm] = start.split('-').map(Number);
    const [ey, em] = end.split('-').map(Number);
    return (ey - sy) * 12 + em - sm < 0 || (ey - sy) * 12 + em - sm > 23;
  }, [start, end]);
  const rangeQuery = useAccountantReport(
    { productId: productId ?? 0, start, end },
    !closingId && !invalidRange
  );
  const closingQuery = useClosingAccountantReport(closingId);
  const sync = useSyncClosingAccountantReport();
  const response = closingId ? closingQuery.data : rangeQuery.data;
  const report = response?.data;
  const context = response?.context ?? {};

  useEffect(() => {
    if (!productId && products[0]) {
      setProductId(products[0].id);
    }
  }, [productId, products]);

  useEffect(() => {
    if (closingId && report?.start && report?.end) {
      setProductId(report.productId);
      setStart(monthValue(report.start.month, report.start.year));
      setEnd(monthValue(report.end.month, report.end.year));
    }
  }, [closingId, report]);

  const isLoading = closingId ? closingQuery.isLoading : rangeQuery.isLoading;

  async function download(format: 'xlsx' | 'pdf') {
    if (!productId || invalidRange) {
      return;
    }
    setExporting(format);
    try {
      await financialOpsService.downloadAccountantReport({ productId, start, end, format });
    } catch (error) {
      toast.error(errorMessage(error, 'Falha ao exportar o relatório do contador.'));
    } finally {
      setExporting(null);
    }
  }

  function synchronize() {
    if (!closingId) {
      return;
    }
    sync.mutate(closingId, {
      onSuccess: () => toast.success('Relatório sincronizado.'),
      onError: (error) => toast.error(errorMessage(error, 'Falha ao sincronizar o relatório.')),
    });
  }

  const events = report?.events ?? [];
  const coverage = coverageDetails(context.coverage);
  const refundEvents = events.filter((event) =>
    ['REFUND', 'DISPUTE', 'DISPUTE_REVERSAL'].includes(event.type)
  );
  const problemEvents = events.filter((event) => event.type === 'PAYMENT_FAILURE');

  return (
    <div className="space-y-6 max-w-6xl">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wider text-ink-secondary">
            DocuGen · Solvyxa Lab Ltda.
          </p>
          <h2 className="text-xl font-semibold text-ink">Relatório do Contador</h2>
          <p className="text-sm text-ink-secondary">
            Ledger Stripe em BRL, competência America/Sao_Paulo. Dados auditáveis, sem payloads
            brutos.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {closingId && (
            <Button
              size="sm"
              variant="secondary"
              loading={sync.isPending}
              onClick={synchronize}
              icon={<RefreshCw size={14} />}
            >
              Sincronizar
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            loading={exporting === 'xlsx'}
            disabled={!report || invalidRange}
            onClick={() => download('xlsx')}
            icon={<Download size={14} />}
          >
            Exportar Excel
          </Button>
          <Button
            size="sm"
            variant="ghost"
            loading={exporting === 'pdf'}
            disabled={!report || invalidRange}
            onClick={() => download('pdf')}
            icon={<Download size={14} />}
          >
            Exportar PDF
          </Button>
        </div>
      </header>

      {!closingId && (
        <Card padding="sm" className="flex flex-wrap items-end gap-3">
          <label className="text-sm text-ink-secondary">
            Produto
            <select
              className="input mt-1 block"
              value={productId ?? ''}
              onChange={(event) => setProductId(Number(event.target.value))}
            >
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-ink-secondary">
            Início
            <input
              aria-label="Início"
              className="input mt-1 block"
              type="month"
              value={start}
              onChange={(event) => setStart(event.target.value)}
            />
          </label>
          <label className="text-sm text-ink-secondary">
            Fim
            <input
              aria-label="Fim"
              className="input mt-1 block"
              type="month"
              value={end}
              onChange={(event) => setEnd(event.target.value)}
            />
          </label>
          {invalidRange && (
            <p className="text-sm text-danger">O intervalo deve ser de até 24 meses.</p>
          )}
        </Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : !report ? (
        <EmptyState
          title="Relatório indisponível"
          body="Configure o billing contábil do DocuGen para a competência solicitada."
        />
      ) : (
        <>
          <Card padding="sm" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm">
            <div>
              <p className="text-ink-secondary">Empresa</p>
              <p className="font-medium text-ink">{context.companyName || 'Solvyxa Lab Ltda.'}</p>
            </div>
            <div>
              <p className="text-ink-secondary">Produto</p>
              <p className="font-medium text-ink">
                {context.productName ||
                  products.find((p) => p.id === report.productId)?.name ||
                  'DocuGen'}
              </p>
            </div>
            <div>
              <p className="text-ink-secondary">Período</p>
              <p className="font-medium text-ink">{periodLabel(report)}</p>
            </div>
            <div>
              <p className="text-ink-secondary">Última sincronização</p>
              <p className="font-medium text-ink">
                {formatDateBR(coverage.lastSyncAt, 'Não registrada')}
              </p>
            </div>
          </Card>

          <div className="space-y-2">
            <div className="rounded-md border border-warning bg-warning/10 px-3 py-2 text-sm text-ink">
              <strong>Escopo:</strong> metadata exigida{' '}
              <code className="font-mono">{context.metadata || 'app=docugen'}</code>; moeda BRL e
              fuso America/Sao_Paulo.
            </div>
            {coverage.excludedCount > 0 && (
              <div className="rounded-md border border-warning bg-warning/10 px-3 py-2 text-sm text-ink">
                <strong>Cobertura:</strong> {coverage.excludedCount} evento(s) excluído(s),{' '}
                {formatCurrency(coverage.excludedAmount)} fora do escopo.
              </div>
            )}
            {report.summary.estimatedDas === null && (
              <div className="rounded-md border border-warning bg-warning/10 px-3 py-2 text-sm text-ink">
                <strong>DAS não calculado:</strong>{' '}
                {report.summary.dasReason || 'Configuração fiscal ausente para o período.'}
              </div>
            )}
            {report.subscriptionCounts.statusCoverage &&
              !report.subscriptionCounts.statusCoverage.available && (
                <div className="rounded-md border border-warning bg-warning/10 px-3 py-2 text-sm text-ink">
                  <strong>Indicadores de status indisponíveis:</strong>{' '}
                  {report.subscriptionCounts.statusCoverage.reason ||
                    'Faltam snapshots suficientes para a competência.'}
                </div>
              )}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Receita bruta', report.summary.grossSubscriptionRevenue],
              ['Reembolsos', report.summary.refunds],
              ['Disputas', report.summary.disputes],
              ['Reversões de disputas', report.summary.disputeReversals ?? 0],
              ['Taxas Stripe', report.summary.stripeFees],
              ['Líquido Stripe', report.summary.stripeNet],
              ['Payouts', report.summary.payouts],
              ['Falhas de pagamento', report.summary.failedPayments],
              ['DAS estimado', report.summary.estimatedDas],
            ].map(([label, value]) => (
              <Card key={String(label)} padding="sm">
                <p className="text-xs text-ink-secondary">{label}</p>
                <p className="font-semibold text-ink">{formatCurrency(value as number | null)}</p>
              </Card>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Ativas no fim', report.subscriptionCounts.activeAtEnd],
              ['Novas', report.subscriptionCounts.newSubscriptions],
              ['Canceladas', report.subscriptionCounts.canceledSubscriptions],
              ['Inadimplentes', report.subscriptionCounts.delinquentAtEnd],
            ].map(([label, value]) => (
              <Card key={String(label)} padding="sm">
                <p className="text-xs text-ink-secondary">{label}</p>
                <p className="font-semibold text-ink">{statusCount(value as number | null)}</p>
              </Card>
            ))}
          </div>

          <div className="flex gap-1 overflow-x-auto border-b border-border" role="tablist">
            {TABS.map(([id, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={`px-3 py-2 text-sm whitespace-nowrap ${tab === id ? 'border-b-2 border-ink text-ink font-medium' : 'text-ink-secondary'}`}
              >
                {label}
              </button>
            ))}
          </div>
          {tab === 'summary' && (
            <Card padding="sm">
              <h3 className="font-medium text-ink mb-3">Resumo por competência</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-ink-secondary">
                    <tr>
                      <th className="text-left py-2">Competência</th>
                      <th className="text-right">Receita</th>
                      <th className="text-right">Reembolsos</th>
                      <th className="text-right">Disputas</th>
                      <th className="text-right">Ativas</th>
                      <th className="text-right">Novas</th>
                      <th className="text-right">Canceladas</th>
                      <th className="text-right">Inadimplentes</th>
                      <th className="text-right">DAS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.monthly.map((month) => (
                      <tr key={`${month.year}-${month.month}`} className="border-t border-border">
                        <td className="py-2">
                          {MONTHS[month.month - 1]}/{month.year}
                        </td>
                        <td className="text-right">
                          {formatCurrency(month.summary.grossSubscriptionRevenue)}
                        </td>
                        <td className="text-right">{formatCurrency(month.summary.refunds)}</td>
                        <td className="text-right">{formatCurrency(month.summary.disputes)}</td>
                        <td className="text-right">
                          {statusCount(month.subscriptionCounts.activeAtEnd)}
                        </td>
                        <td className="text-right">{month.subscriptionCounts.newSubscriptions}</td>
                        <td className="text-right">
                          {month.subscriptionCounts.canceledSubscriptions}
                        </td>
                        <td className="text-right">
                          {statusCount(month.subscriptionCounts.delinquentAtEnd)}
                        </td>
                        <td className="text-right">
                          {month.summary.estimatedDas === null
                            ? month.summary.dasReason
                            : formatCurrency(month.summary.estimatedDas)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          {tab === 'users' && (
            <EventTable
              events={events.filter((event) => event.stripeCustomerId)}
              empty="Nenhum billing por usuário no período"
            />
          )}
          {tab === 'subscriptions' && (
            <EventTable events={report.subscriptions} empty="Nenhuma assinatura no período" />
          )}
          {tab === 'problems' && (
            <EventTable events={problemEvents} empty="Nenhum pagamento problemático no período" />
          )}
          {tab === 'refunds' && (
            <EventTable events={refundEvents} empty="Nenhum reembolso ou disputa no período" />
          )}
          {tab === 'payouts' && (
            <EventTable events={report.payouts ?? []} empty="Nenhum payout no período" />
          )}
        </>
      )}
    </div>
  );
}
